import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Paper,
  Avatar,
  useTheme,
  alpha
} from '@mui/material';
import Header from '../components/common/Header';
import Footer from '../components/common/Footer';
import SearchIcon from '@mui/icons-material/Search';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import NewspaperIcon from '@mui/icons-material/Newspaper';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BusinessIcon from '@mui/icons-material/Business';
import SecurityIcon from '@mui/icons-material/Security';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';

const NewHome = () => {
  const navigate = useNavigate();
  const theme = useTheme();

  const categories = [
    {
      name: 'Mild Steel',
      image: 'https://images.unsplash.com/photo-1565857923431-eb958c72e730?w=400',
      description: 'Sheets, Plates, Coils',
    },
    {
      name: 'Non Ferrous',
      image: 'https://images.unsplash.com/photo-1611085583191-a3b181a88401?w=400',
      description: 'Aluminum, Copper, Brass',
    },
    {
      name: 'Stainless Steel',
      image: 'https://images.unsplash.com/photo-1601574968106-b312ac309953?w=400',
      description: 'Pipes, Fittings, Sheets',
    },
    {
      name: 'Pipes & Tubes',
      image: 'https://images.unsplash.com/photo-1590846406792-0adc7f938f1d?w=400',
      description: 'MS, SS, GI Pipes',
    },
    {
      name: 'Construction Materials',
      image: 'https://images.unsplash.com/photo-1504917595217-d4dc5ebe6122?w=400',
      description: 'TMT, Cement, Aggregates',
    },
    {
      name: 'Industrial Supplies',
      image: 'https://images.unsplash.com/photo-1581092918056-0c4c3acd3789?w=400',
      description: 'Tools, Equipment, Safety',
    },
  ];

  const services = [
    {
      icon: <SearchIcon sx={{ fontSize: 40 }} />,
      title: 'Easy Procurement',
      description: 'Browse and order from 10,000+ products with real-time pricing and availability',
    },
    {
      icon: <TrendingUpIcon sx={{ fontSize: 40 }} />,
      title: 'Price Intelligence',
      description: 'Get market insights and competitive pricing to make informed decisions',
    },
    {
      icon: <NewspaperIcon sx={{ fontSize: 40 }} />,
      title: 'Market News',
      description: 'Stay updated with latest industry trends, news, and market movements',
    },
    {
      icon: <AccountBalanceIcon sx={{ fontSize: 40 }} />,
      title: 'Credit Access',
      description: 'Flexible payment terms and credit options to support your business growth',
    },
    {
      icon: <LocalShippingIcon sx={{ fontSize: 40 }} />,
      title: 'Reliable Delivery',
      description: 'Pan-India delivery network ensuring timely and safe delivery of materials',
    },
  ];

  const whyChooseUs = [
    {
      icon: <CheckCircleIcon sx={{ fontSize: 50, color: '#07cdbe' }} />,
      title: 'Quality Assured',
      description: 'All materials tested and certified to meet industry standards',
    },
    {
      icon: <BusinessIcon sx={{ fontSize: 50, color: '#07cdbe' }} />,
      title: 'Trusted by 10,000+ Businesses',
      description: 'Serving SMEs and large enterprises across India',
    },
    {
      icon: <SecurityIcon sx={{ fontSize: 50, color: '#07cdbe' }} />,
      title: 'Secure Transactions',
      description: 'Safe and encrypted payment gateway for all transactions',
    },
    {
      icon: <SupportAgentIcon sx={{ fontSize: 50, color: '#07cdbe' }} />,
      title: '24/7 Customer Support',
      description: 'Dedicated support team to assist you at every step',
    },
  ];

  const testimonials = [
    {
      name: 'Rajesh Kumar',
      company: 'Kumar Steel Industries',
      feedback: 'Loha Mart has transformed our procurement process. The quality of materials and timely delivery is exceptional.',
      rating: 5,
    },
    {
      name: 'Priya Sharma',
      company: 'Sharma Construction',
      feedback: 'The credit facility and competitive pricing have helped us scale our business significantly. Highly recommended!',
      rating: 5,
    },
    {
      name: 'Amit Patel',
      company: 'Patel Manufacturing',
      feedback: 'Excellent platform with a wide range of products. The customer support team is always helpful and responsive.',
      rating: 5,
    },
  ];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <Header />

      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #006efa 0%, #07cdbe 100%)',
          color: 'white',
          py: { xs: 8, md: 12 },
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={7}>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: '2rem', md: '3.5rem' },
                  fontWeight: 700,
                  mb: 2,
                }}
              >
                India's Largest B2B Raw Materials Procurement Platform
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  fontSize: { xs: '1rem', md: '1.5rem' },
                  mb: 4,
                  opacity: 0.95,
                }}
              >
                Empowering SMEs with quality materials, competitive pricing, and flexible credit
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => navigate('/products')}
                  sx={{
                    backgroundColor: 'white',
                    color: '#006efa',
                    px: 4,
                    py: 1.5,
                    fontSize: '1.1rem',
                    '&:hover': {
                      backgroundColor: alpha('#ffffff', 0.9),
                    },
                  }}
                >
                  Explore Products
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => navigate('/request-quote')}
                  sx={{
                    borderColor: 'white',
                    color: 'white',
                    px: 4,
                    py: 1.5,
                    fontSize: '1.1rem',
                    '&:hover': {
                      borderColor: 'white',
                      backgroundColor: alpha('#ffffff', 0.1),
                    },
                  }}
                >
                  Request Quote
                </Button>
              </Box>

              {/* Stats */}
              <Grid container spacing={3} sx={{ mt: 4 }}>
                <Grid item xs={4}>
                  <Typography variant="h4" fontWeight={700}>
                    500K+
                  </Typography>
                  <Typography variant="body2">Orders Delivered</Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="h4" fontWeight={700}>
                    10K+
                  </Typography>
                  <Typography variant="body2">SMEs Empowered</Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="h4" fontWeight={700}>
                    26
                  </Typography>
                  <Typography variant="body2">States Covered</Typography>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Categories Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography
          variant="h3"
          align="center"
          gutterBottom
          sx={{ mb: 1 }}
        >
          Explore Categories
        </Typography>
        <Typography
          variant="body1"
          align="center"
          color="text.secondary"
          sx={{ mb: 6 }}
        >
          Browse our wide range of industrial raw materials
        </Typography>

        <Grid container spacing={3}>
          {categories.map((category, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Card
                sx={{
                  height: '100%',
                  cursor: 'pointer',
                  transition: 'transform 0.3s, box-shadow 0.3s',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: 6,
                  },
                }}
                onClick={() => navigate('/products')}
              >
                <CardMedia
                  component="img"
                  height="180"
                  image={category.image}
                  alt={category.name}
                />
                <CardContent>
                  <Typography variant="h6" fontWeight={600} gutterBottom>
                    {category.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {category.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Services Section */}
      <Box sx={{ backgroundColor: '#f5f5f5', py: 8 }}>
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            align="center"
            gutterBottom
            sx={{ mb: 1 }}
          >
            Our Services
          </Typography>
          <Typography
            variant="body1"
            align="center"
            color="text.secondary"
            sx={{ mb: 6 }}
          >
            Comprehensive solutions for all your business needs
          </Typography>

          <Grid container spacing={4}>
            {services.map((service, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 4,
                    height: '100%',
                    textAlign: 'center',
                    transition: 'all 0.3s',
                    border: '1px solid',
                    borderColor: 'divider',
                    '&:hover': {
                      borderColor: 'primary.main',
                      boxShadow: 3,
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: 'inline-flex',
                      p: 2,
                      borderRadius: '50%',
                      backgroundColor: alpha(theme.palette.primary.main, 0.1),
                      color: 'primary.main',
                      mb: 2,
                    }}
                  >
                    {service.icon}
                  </Box>
                  <Typography variant="h6" fontWeight={600} gutterBottom>
                    {service.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {service.description}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Why Choose Us Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography
          variant="h3"
          align="center"
          gutterBottom
          sx={{ mb: 1 }}
        >
          Why Choose Loha Mart?
        </Typography>
        <Typography
          variant="body1"
          align="center"
          color="text.secondary"
          sx={{ mb: 6 }}
        >
          Your trusted partner for industrial materials procurement
        </Typography>

        <Grid container spacing={4}>
          {whyChooseUs.map((item, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Box sx={{ textAlign: 'center' }}>
                <Box sx={{ mb: 2 }}>
                  {item.icon}
                </Box>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  {item.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {item.description}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Testimonials Section */}
      <Box sx={{ backgroundColor: '#f5f5f5', py: 8 }}>
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            align="center"
            gutterBottom
            sx={{ mb: 1 }}
          >
            What Our Customers Say
          </Typography>
          <Typography
            variant="body1"
            align="center"
            color="text.secondary"
            sx={{ mb: 6 }}
          >
            Success stories from businesses we've empowered
          </Typography>

          <Grid container spacing={4}>
            {testimonials.map((testimonial, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Paper
                  elevation={2}
                  sx={{
                    p: 4,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 3, fontStyle: 'italic' }}>
                    "{testimonial.feedback}"
                  </Typography>
                  <Box sx={{ mt: 'auto', display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar sx={{ bgcolor: 'primary.main' }}>
                      {testimonial.name.charAt(0)}
                    </Avatar>
                    <Box>
                      <Typography variant="subtitle2" fontWeight={600}>
                        {testimonial.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {testimonial.company}
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #006efa 0%, #07cdbe 100%)',
          color: 'white',
          py: 8,
        }}
      >
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center' }}>
            <PhoneAndroidIcon sx={{ fontSize: 60, mb: 2 }} />
            <Typography variant="h3" gutterBottom fontWeight={700}>
              Get Started Today
            </Typography>
            <Typography variant="h6" sx={{ mb: 4, opacity: 0.9 }}>
              Join thousands of businesses transforming their procurement process
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                size="large"
                onClick={() => navigate('/register')}
                sx={{
                  backgroundColor: 'white',
                  color: '#006efa',
                  px: 4,
                  py: 1.5,
                  fontSize: '1.1rem',
                  '&:hover': {
                    backgroundColor: alpha('#ffffff', 0.9),
                  },
                }}
              >
                Register Now
              </Button>
              <Button
                variant="outlined"
                size="large"
                onClick={() => navigate('/contact')}
                sx={{
                  borderColor: 'white',
                  color: 'white',
                  px: 4,
                  py: 1.5,
                  fontSize: '1.1rem',
                  '&:hover': {
                    borderColor: 'white',
                    backgroundColor: alpha('#ffffff', 0.1),
                  },
                }}
              >
                Contact Sales
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>

      <Footer />
    </Box>
  );
};

export default NewHome;
